'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import React from 'react';
import Link from '@eliadmin/core/src/components/link';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import { ProductBadge } from '../detail/product-badge';

type ProductCardColorsProps = {
  product: Product;
  className?: { attribute?: string; attribute_divider?: string };
};

function ProductCardColors({ product, className }: ProductCardColorsProps) {
  const colorAttr = product.attributes?.find(a => a.name === 'Farba');
  const colors = colorAttr?.values;

  if (!colors || colors.length === 0) {
    return <div className="h-[18px]" />;
  }

  return (
    <div className="flex items-center justify-start gap-2">
      {/* <p className={cn('h-[18px] text-sm text-[#666]', className?.attribute)}>{colors[0].name}</p>
      <div
        className={cn('h-[3px] w-[3px] rounded-full bg-[#D1D1D1]', className?.attribute_divider)}
      /> */}
      <p className={cn('h-[18px] text-sm text-[#666]', className?.attribute)}>
        {colors.length} farieb
      </p>
    </div>
  );
}

export type ProductCardProps = {
  product: Product;
  white?: boolean;
  imageSizes?: string;
  className?: {
    main?: string;
    header_container?: string;
    image_link?: string;
    image?: string;
    footer_container?: string;
    name?: string;
    price?: string;
    discounted_price?: string;
    attribute?: string;
    attribute_divider?: string;
  };
};

function ProductCardInternal({ product, white, className, imageSizes }: ProductCardProps) {
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);

  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, { isWholesaler });

  return (
    <div
      className={cn(
        'group transition-all',
        'col-span-1 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6',
        className?.main,
      )}
    >
      <div
        className={cn(
          'relative h-[280px] w-full overflow-hidden @[175px]:h-[364px]',
          className?.header_container,
        )}
      >
        <div className="absolute left-2 top-2 z-10 flex w-full flex-wrap items-start justify-start gap-2">
          {priceDisplay.showPreviousPrice && (
            <ProductBadge
              text={`-${priceDisplay.discountLabel}%`}
              background="#FF4B00"
              color="#FFFFFF"
            />
          )}
          {product.tags?.map(tag => (
            <ProductBadge
              key={tag.name}
              text={tag.name}
              background={tag.color ?? '#000000'}
              color={getContrastYIQ(tag.color ?? '#000000')}
            />
          ))}
        </div>
        <Link
          prefetchOnHover
          prefetch={false}
          href={productUrl}
          aria-label={product.name}
          className={cn('block h-full w-full', className?.image_link)}
        >
          <Image
            src={image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(image, product?.name)}
            width={0}
            height={0}
            className={cn(
              'absolute inset-0 z-0 h-full w-full object-contain object-center',
              className?.image,
            )}
            sizes={
              imageSizes ??
              '(min-width: 1200px) 282px, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, (min-width: 640px) 50vw, 100vw'
            }
          />
        </Link>
        <div
          className={cn(
            'absolute bottom-3 left-1/2 flex w-[calc(100%-24px)] -translate-x-1/2 translate-y-[150%] items-center justify-between gap-2 bg-white p-2',
            'transition-all group-hover:translate-y-0',
            'ea-product-card__actions-bar-container',
            className?.footer_container,
          )}
        >
          <AddToCartButton
            product={product}
            showIcon={!!templateSettings.product_teaser?.cart_icon}
            sellable={isSellable(product, 1)}
            quantity={1}
            customText={t('text1115')}
            className={cn('!max-h-10 flex-grow', 'ea-product-card__add-to-cart-button')}
          />
          <AddToFavoritesButton
            slug={product.slug}
            isFavorite={product.is_favorite ?? false}
            productId={product.id}
            buttonSize={'icon-md'}
            buttonVariant={'secondary'}
          />
        </div>
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-3">
        <div className="flex w-full flex-col items-start justify-start gap-2">
          <strong
            className={cn(
              'line-clamp-2 h-[42px] text-lg font-medium leading-[115%] tracking-tight text-black',
              white && 'text-white',
              className?.name,
            )}
          >
            {product.name}
          </strong>
          <ProductCardColors
            product={product}
            className={{
              ...className,
              attribute: cn(white && 'text-white/70', className?.attribute),
            }}
          />
        </div>
        <ProductPrice
          product={product}
          className={{
            priceBeforeDiscount: cn(white && 'text-white/70', className?.discounted_price),
            priceAfterDiscount: cn(white && 'text-white', className?.price),
          }}
        />
      </div>
    </div>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider
      state={{ product: props.product, selectedQuantity: props.product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
